import React from 'react';

import { observer } from 'mobx-react';

import CloseIcon from '@mui/icons-material/Close';

import Grid from 'components/Grid';
import { ColumnDefinition, FilterType } from 'components/Grid/types';
import { VatixError } from 'components/Error/types';

import InitialAvatar from 'components/Avatar/InitialAvatar';

import { useStore } from 'utils/hooks/store';

import { MemberRow } from 'utils/api/modules/memberGroups';

import { canManageMembers } from 'containers/MemberGroups/utils/membership';

import { MemberNameCell, MemberRemove, MembersGridWrap } from './styles';

const GroupMemberTable: React.FunctionComponent<{ temporaryFilters?: FilterType[] }> = observer(
  ({ temporaryFilters }) => {
    const { memberGroupDetails, notification } = useStore();
    const { group, membersVersion } = memberGroupDetails;
    const groupUuid = group?.uuid;

    const canRemoveMembers = group ? canManageMembers(group) : false;

    const refreshRef = React.useRef<(() => void) | null>(null);

    React.useEffect(() => {
      refreshRef.current?.();
    }, [membersVersion]);

    if (!groupUuid) {
      return null;
    }

    const basicColumns = [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1.4,
        minWidth: 220,
        renderCell: ({ row }: { row: MemberRow }) => (
          <MemberNameCell>
            <InitialAvatar name={row.name} size="small" />
            <span>{row.name}</span>
          </MemberNameCell>
        ),
      },
      {
        field: 'jobTitle',
        headerName: 'Job title',
        flex: 1,
        minWidth: 160,
        // OrderingFilter keys off the model field, so the server sort token is job_title.
        sortingField: 'job_title',
        renderCell: ({ row }: { row: MemberRow }) => row.jobTitle || '—',
      },
      {
        field: 'department',
        headerName: 'Department',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }: { row: MemberRow }) => row.department?.name || '—',
      },
      {
        field: 'site',
        headerName: 'Site',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }: { row: MemberRow }) => row.site?.name || '—',
      },
      ...(canRemoveMembers
        ? [
            {
              field: 'actions',
              type: 'actions',
              headerName: '',
              flex: 0,
              width: 64,
              sortable: false,
              renderCell: ({ row }: { row: MemberRow }) => (
                <MemberRemove
                  type="button"
                  aria-label={`Remove ${row.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    memberGroupDetails.removeMember(row.uuid, row.name);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </MemberRemove>
              ),
            },
          ]
        : []),
    ] as unknown as ColumnDefinition[];

    return (
      <MembersGridWrap>
        <Grid
          key={`${groupUuid}-${canRemoveMembers}`}
          basicColumns={basicColumns}
          dataURL={`member-groups/${groupUuid}/members/`}
          sortBy={{ field: 'name', sort: 'asc' }}
          temporaryFilters={temporaryFilters}
          showHeader={false}
          customNoRowsText="No members to show"
          onRefreshCallback={(callback) => {
            refreshRef.current = callback;
          }}
          onError={(_error: VatixError) => notification.enqueueErrorSnackbar('Could not load members')}
          disableColumnSelector
          disableColumnFilter
          disableExport
          disableSavePreferences
        />
      </MembersGridWrap>
    );
  }
);

export default GroupMemberTable;
